'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import StarRating from '@/components/ui/StarRating';

const movie = {
  id: 'movie-oppenheimer',
  title: 'Oppenheimer',
  originalTitle: 'Oppenheimer',
  year: 2023,
  runtime: 180,
  genres: ['Drama', 'History', 'Thriller', 'Biography'],
  director: 'Christopher Nolan',
  writers: ['Christopher Nolan'],
  cast: [
  { id: 'cast-001', name: 'Cillian Murphy', role: 'J. Robert Oppenheimer', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a3897225-1763292675321.png", avatarAlt: 'Professional headshot of middle-aged man with serious expression' },
  { id: 'cast-002', name: 'Emily Blunt', role: 'Katherine Oppenheimer', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a74955ab-1772534014749.png", avatarAlt: 'Professional headshot of woman with confident expression' },
  { id: 'cast-003', name: 'Matt Damon', role: 'Leslie Groves', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_172fcd53a-1763299680950.png", avatarAlt: 'Professional headshot of man with friendly smile' },
  { id: 'cast-004', name: 'Robert Downey Jr.', role: 'Lewis Strauss', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bfef8bd5-1763295388609.png", avatarAlt: 'Professional headshot of confident man in dark setting' },
  { id: 'cast-005', name: 'Florence Pugh', role: 'Jean Tatlock', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18d89f49b-1763301927007.png", avatarAlt: 'Professional headshot of young woman with expressive eyes' },
  { id: 'cast-006', name: 'Josh Hartnett', role: 'Ernest Lawrence', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_140a3b984-1772780037635.png", avatarAlt: 'Professional headshot of man with thoughtful gaze' }],

  rating: 4.8,
  communityScore: 96,
  reviewCount: 22340,
  watchlistCount: 48920,
  watchedCount: 312450,
  synopsis: 'The story of American scientist J. Robert Oppenheimer and his role in the development of the atomic bomb during World War II. Spanning decades of Oppenheimer\'s life, the film explores the moral complexities of scientific discovery, political manipulation, and the devastating consequences of creating the world\'s most destructive weapon. A biographical thriller of extraordinary ambition and craft.',
  streamingOn: ['IMAX', 'Peacock', 'Digital HD'],
  languages: ['English', 'German', 'French'],
  country: 'United States',
  releaseDate: 'July 21, 2023',
  budget: '$100M',
  boxOffice: '$952M',
  awards: 'Academy Award for Best Picture · Best Director · Best Actor',
  poster: "https://img.rocket.new/generatedImages/rocket_gen_img_189857718-1785350644591.png",
  posterAlt: 'Abstract nuclear explosion light patterns representing Oppenheimer film',
  backdrop: "https://img.rocket.new/generatedImages/rocket_gen_img_177baf7c8-1782310271035.png",
  backdropAlt: 'Dramatic light explosion representing nuclear science',
  ratingDistribution: [
  { stars: 5, count: 14820, pct: 66 },
  { stars: 4, count: 5810, pct: 26 },
  { stars: 3, count: 1340, pct: 6 },
  { stars: 2, count: 250, pct: 1 },
  { stars: 1, count: 120, pct: 0.5 }]

};

export default function MovieDetailHero() {
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watched, setWatched] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [trailerOpen, setTrailerOpen] = useState(false);

  return (
    <>
      {/* Backdrop */}
      <div className="relative h-80 overflow-hidden">
        <AppImage src={movie.backdrop} alt={movie.backdropAlt} fill priority className="object-cover" sizes="100vw" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
      </div>

      {/* Main content */}
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 -mt-48 relative z-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Poster */}
          <div className="flex-shrink-0 w-48 md:w-56 lg:w-64">
            <div className="relative w-full aspect-[2/3] rounded-2xl overflow-hidden shadow-modal ring-1 ring-white/10">
              <AppImage src={movie.poster} alt={movie.posterAlt} fill priority className="object-cover" sizes="256px" />
            </div>

            {/* Streaming badges */}
            <div className="mt-3 space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-500">Available on</p>
              {movie.streamingOn.map((platform) =>
              <div key={`stream-${platform}`} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border">
                  <Icon name="PlayCircleIcon" size={14} className="text-primary" />
                  <span className="text-xs font-600 text-foreground">{platform}</span>
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="flex-1 min-w-0 pt-4 lg:pt-12">
            {/* Title */}
            <div className="mb-3">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="px-2 py-0.5 rounded-md text-xs font-700 badge-trending">Top Rated</span>
                <span className="px-2 py-0.5 rounded-md text-xs font-700 badge-new text-white">2023</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-800 text-foreground leading-tight">{movie.title}</h1>
            </div>

            {/* Meta row */}
            <div className="flex items-center gap-3 flex-wrap text-sm text-muted-foreground mb-4">
              <span>{movie.year}</span>
              <span className="text-border">·</span>
              <span>{Math.floor(movie.runtime / 60)}h {movie.runtime % 60}m</span>
              <span className="text-border">·</span>
              {movie.genres.map((g) =>
              <span key={`detail-genre-${g}`} className="genre-chip px-2 py-0.5 text-xs rounded-full">{g}</span>
              )}
            </div>

            {/* Rating section */}
            <div className="flex items-start gap-6 flex-wrap mb-5">
              {/* Community score */}
              <div className="text-center">
                <div className="text-4xl font-800 text-accent font-mono-nums">{movie.rating.toFixed(1)}</div>
                <StarRating value={movie.rating} size={14} />
                <div className="text-xs text-muted-foreground mt-1">{movie.reviewCount.toLocaleString()} reviews</div>
              </div>

              <div className="w-px h-16 bg-border" />

              {/* Community meter */}
              <div className="text-center">
                <div className="text-4xl font-800 text-green-400 font-mono-nums">{movie.communityScore}%</div>
                <div className="text-xs text-muted-foreground mt-1 font-500">Community Score</div>
                <div className="text-xs text-muted-foreground">{movie.watchedCount.toLocaleString()} watched</div>
              </div>

              <div className="w-px h-16 bg-border hidden sm:block" />

              {/* User rating */}
              <div>
                <div className="text-xs text-muted-foreground font-500 mb-2">Your Rating</div>
                <StarRating value={userRating} interactive onChange={setUserRating} size={22} showValue={userRating > 0} />
                {userRating === 0 && <p className="text-xs text-muted-foreground/60 mt-1">Click to rate</p>}
              </div>
            </div>

            {/* Rating distribution */}
            <div className="mb-5 max-w-xs">
              {movie.ratingDistribution.map((row) =>
              <div key={`dist-${row.stars}`} className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-muted-foreground w-4 text-right font-mono-nums">{row.stars}</span>
                  <Icon name="StarIcon" variant="solid" size={10} className="rating-star-filled flex-shrink-0" />
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                    className="h-full bg-accent rounded-full progress-bar-fill"
                    style={{ width: `${row.pct}%` }} />
                  
                  </div>
                  <span className="text-xs text-muted-foreground w-8 font-mono-nums">{row.pct}%</span>
                </div>
              )}
            </div>

            {/* Synopsis */}
            <p className="text-sm text-muted-foreground leading-relaxed mb-5 max-w-2xl">{movie.synopsis}</p>

            {/* Director/Awards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 max-w-xl">
              <div>
                <p className="text-xs text-muted-foreground/60 uppercase tracking-wider mb-0.5">Director</p>
                <p className="text-sm font-600 text-foreground">{movie.director}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground/60 uppercase tracking-wider mb-0.5">Release</p>
                <p className="text-sm font-600 text-foreground">{movie.releaseDate}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-muted-foreground/60 uppercase tracking-wider mb-0.5">Awards</p>
                <p className="text-sm font-500 text-gold">{movie.awards}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setTrailerOpen(true)}
                className="btn-primary flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-700">
                
                <Icon name="PlayIcon" variant="solid" size={16} />
                Watch Trailer
              </button>
              <button
                onClick={() => setInWatchlist(!inWatchlist)}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-600 transition-all duration-150 ${
                inWatchlist ? 'bg-primary/15 text-primary border border-primary/30' : 'btn-ghost'}`
                }>
                
                <Icon name={inWatchlist ? 'BookmarkSolidIcon' as Parameters<typeof Icon>[0]['name'] : 'BookmarkIcon'} size={16} />
                {inWatchlist ? 'In Watchlist' : 'Add to Watchlist'}
              </button>
              <button
                onClick={() => setWatched(!watched)}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-600 transition-all duration-150 ${
                watched ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'btn-ghost'}`
                }>
                
                <Icon name={watched ? 'CheckCircleIcon' : 'EyeIcon'} size={16} />
                {watched ? 'Watched' : 'Mark as Watched'}
              </button>
              <button className="btn-ghost p-3 rounded-xl">
                <Icon name="ShareIcon" size={16} className="text-muted-foreground" />
              </button>
            </div>
          </div>
        </div>

        {/* Cast */}
        <div className="mt-10">
          <h2 className="text-lg font-700 text-foreground mb-4">Cast & Crew</h2>
          <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
            {movie.cast.map((person) =>
            <div key={person.id} className="flex-shrink-0 text-center w-20">
                <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-2 ring-2 ring-border">
                  <AppImage src={person.avatar} alt={person.avatarAlt} width={64} height={64} className="object-cover w-full h-full" sizes="64px" />
                </div>
                <p className="text-xs font-600 text-foreground leading-tight">{person.name}</p>
                <p className="text-xs text-muted-foreground leading-tight mt-0.5 line-clamp-2">{person.role}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Trailer modal */}
      {trailerOpen &&
      <div className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop" onClick={() => setTrailerOpen(false)}>
          <div className="glass-card-elevated rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-modal scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-700 text-foreground">Official Trailer — {movie.title}</h3>
              <button onClick={() => setTrailerOpen(false)} className="btn-ghost p-2 rounded-lg">
                <Icon name="XMarkIcon" size={16} />
              </button>
            </div>
            <div className="aspect-video bg-muted rounded-xl flex items-center justify-center">
              <div className="text-center">
                <Icon name="PlayCircleIcon" size={48} className="text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Trailer playback requires streaming integration</p>
                {/* Backend integration: embed YouTube/Vimeo trailer URL here */}
              </div>
            </div>
          </div>
        </div>
      }
    </>);

}